/**
 * The school's plans and the payments made against them.
 *
 * Source: the exact two routes the school panel's Membership Plans screen uses
 * — `school/get-plans/{id}` and `school/get-payments/{id}` — called with the
 * school's id from the URL (a school's analytics `school_id` IS its user id).
 * Both are paginated server-side, so each table pages on its own without
 * refetching the other, the same way that screen does it.
 *
 * The backend now lets an admin token read a named school, so the "User not
 * found" answer these routes used to give for any id but the caller's own is
 * gone. Verified against staging for school 1242 on 17 Sep 2026.
 */

import moment from "moment";
import { useEffect, useState } from "react";
import type {
  PaymentRow,
  PlanCampus,
  PlanCompareRow,
  PlanHistoryRow,
  ReceiptView,
} from "shared/components/schoolAnalytics";
import {
  getPaymentHistory,
  getPlanHistory,
} from "shared/services/schoolMembershipService";

export const HISTORY_PAGE_SIZE = 5;

const fmtDate = (value?: string | null) => {
  const m = moment(value ?? undefined);
  return value && m.isValid() ? m.format("D MMM YYYY") : "—";
};

/** "271360" → "271,360"; anything unreadable becomes "—". */
const fmtAmount = (value: any) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-US");
};

/** "jazz_cash" → "Jazz Cash", matching the school panel's own labelling. */
const fmtMethod = (value?: string | null) => {
  if (!value) return "—";
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const capitalize = (value?: string | null) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "—";

/** Plan status: 1 Active, 2 Expired, 3 Pending, anything else not started. */
const statusOfPlan = (value: any) => {
  switch (Number(value)) {
    case 1:
      return "Active";
    case 2:
      return "Expired";
    case 3:
      return "Pending";
    default:
      return "Inactive";
  }
};

/**
 * The plan's own `metadata` block, which carries what it replaced and what it
 * created. The school panel's Plan Details dialog reads exactly these fields.
 */
const COMPARE_FIELDS: [string, string, string?][] = [
  ["Duration", "duration", "Days"],
  ["Student Count", "student_count"],
  ["Teacher Count", "teacher_count"],
  ["Books Count", "books_count"],
];

export const toCompareRows = (plan: any): PlanCompareRow[] => {
  const previous = plan?.metadata?.previous_plan;
  const next = plan?.metadata?.new_plan;
  if (!previous && !next) return [];

  return COMPARE_FIELDS.map(([label, key, unit]) => {
    const before = previous?.[key];
    const after = next?.[key];
    const show = (value: any) =>
      value == null || value === "" ? "—" : `${value}${unit ? ` ${unit}` : ""}`;

    return {
      label,
      previous: show(before),
      next: show(after),
      // The API sends these as a mix of numbers and strings, so compare text.
      changed: String(before ?? "") !== String(after ?? ""),
    };
  });
};

/** `metadata.campus` is a list — a plan can create more than one. */
export const toCampuses = (plan: any): PlanCampus[] =>
  (plan?.metadata?.campus ?? []).map((campus: any) => ({
    title: campus?.title || undefined,
    students: campus?.no_students ?? undefined,
    teachers: campus?.no_teachers ?? undefined,
    city: campus?.city?.name || undefined,
    phone: campus?.phone || undefined,
    address: campus?.address || undefined,
  }));

const toPlanRow = (plan: any): PlanHistoryRow => {
  const hasDiscount =
    Number(plan?.is_discounted) === 1 && plan?.discounted_price != null;

  return {
    id: plan.id,
    name: plan?.name || "—",
    duration: plan?.duration ? `${plan.duration} Days` : "—",
    price: fmtAmount(plan?.price),
    discounted: hasDiscount ? fmtAmount(plan.discounted_price) : "—",
    discountTone: hasDiscount ? "saved" : "none",
    status: statusOfPlan(plan?.status),
    start: fmtDate(plan?.start_date),
    end: fmtDate(plan?.end_date),
  };
};

const toPaymentRow = (payment: any): PaymentRow => ({
  id: payment?.transaction_id || `#${payment?.id}`,
  amount: fmtAmount(payment?.price),
  method: fmtMethod(payment?.transaction_method),
  instalment:
    !payment?.total_installments || payment?.total_installments === 0
      ? "—"
      : `${payment.installment_number} of ${payment.total_installments}`,
  status: capitalize(payment?.status),
  due: fmtDate(payment?.due_date),
  purchased: fmtDate(payment?.created_at),
  // A rejected payment carries the reason; surface it on the row's flag.
  flagged: Boolean(payment?.reason),
  flagNote: payment?.reason || undefined,
});

/** The currency the plan was priced in — levels 1 and 2 are billed in rupees. */
const currencyOf = (row: any) =>
  row?.currency_type === "PKR" || row?.plan_level === 1 || row?.plan_level === 2
    ? "Rs."
    : "$";

function useSchoolPlans(schoolId: number) {
  const [plans, setPlans] = useState<PlanHistoryRow[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansPage, setPlansPage] = useState(0);
  const [plansTotal, setPlansTotal] = useState(0);
  const [currency, setCurrency] = useState("Rs.");

  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  /** transaction id → the receipt the eye button opens: image plus, on a
      rejected payment, the reason the API gives for refusing it. */
  const [receipts, setReceipts] = useState<Record<string, ReceiptView>>({});

  /** The raw plan rows, which SchoolPlanDetail reads straight from the API. */
  const [rawPlans, setRawPlans] = useState<Record<string, any>>({});
  /** plan id → that plan's receipt, for the plan table's invoice button. */
  const [planReceipts, setPlanReceipts] = useState<Record<string, ReceiptView>>(
    {}
  );

  /* -------------------------------- plans -------------------------------- */
  useEffect(() => {
    let cancelled = false;

    if (!Number.isFinite(schoolId)) {
      setPlansLoading(false);
      return;
    }

    setPlansLoading(true);

    getPlanHistory(schoolId, {
      page: plansPage + 1,
      per_page: HISTORY_PAGE_SIZE,
    })
      .then(({ data }: any) => {
        if (cancelled) return;

        const block = data?.data?.plans ?? {};
        const rows: any[] = block?.data ?? [];

        setPlans(rows.map(toPlanRow));
        setPlansTotal(Number(block?.pagination?.total) || rows.length);
        if (rows.length) setCurrency(currencyOf(rows[0]));

        // SchoolPlanDetail reads the API's own fields, so keep the rows whole.
        // The school name it shows sits on the payload's user block, not the
        // plan, so carry it down onto each row.
        setRawPlans(
          rows.reduce((all: Record<string, any>, row: any) => {
            all[String(row?.id)] = {
              ...row,
              school_name: data?.data?.user?.school_name,
            };
            return all;
          }, {})
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, plansPage]);

  /* ------------------------------- payments ------------------------------ */
  useEffect(() => {
    let cancelled = false;

    if (!Number.isFinite(schoolId)) {
      setPaymentsLoading(false);
      return;
    }

    setPaymentsLoading(true);

    getPaymentHistory(schoolId, {
      page: paymentsPage + 1,
      per_page: HISTORY_PAGE_SIZE,
    })
      .then(({ data }: any) => {
        if (cancelled) return;

        const block = data?.data?.transactions ?? {};
        const rows: any[] = block?.data ?? [];

        setPayments(rows.map(toPaymentRow));
        setPaymentsTotal(Number(block?.pagination?.total) || rows.length);
        setReceipts(
          rows.reduce((all: Record<string, ReceiptView>, row: any) => {
            const key = row?.transaction_id || `#${row?.id}`;
            all[key] = {
              transactionId: key,
              image: row?.receipt_image || undefined,
              // Only a rejected payment carries one.
              reason: row?.reason || undefined,
              status: capitalize(row?.status),
            };
            return all;
          }, {})
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setPaymentsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, paymentsPage]);

  /* ------------------- plan → receipt, for the plan table ----------------- */
  // The plan and payment tables page independently, so the payments loaded for
  // the table below are rarely the ones the visible plans belong to. This pulls
  // the whole list once - it is one page of a couple of dozen rows - purely to
  // answer "which receipt belongs to this plan".
  useEffect(() => {
    let cancelled = false;

    if (!Number.isFinite(schoolId)) return undefined;

    getPaymentHistory(schoolId, { page: 1, per_page: 200 })
      .then(({ data }: any) => {
        if (cancelled) return;

        const rows: any[] = data?.data?.transactions?.data ?? [];

        setPlanReceipts(
          rows.reduce((all: Record<string, ReceiptView>, row: any) => {
            const planId = String(row?.plan_id ?? "");
            // Newest first, and an instalment plan has several payments: keep
            // the first one seen so the button opens the latest receipt.
            if (!planId || all[planId]) return all;

            all[planId] = {
              transactionId: row?.transaction_id || `#${row?.id}`,
              image: row?.receipt_image || undefined,
              reason: row?.reason || undefined,
              status: capitalize(row?.status),
            };
            return all;
          }, {})
        );
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  return {
    plans,
    plansLoading,
    plansPage,
    plansTotal,
    setPlansPage,
    payments,
    paymentsLoading,
    paymentsPage,
    paymentsTotal,
    setPaymentsPage,
    receipts,
    rawPlans,
    planReceipts,
    currency,
  };
}

export default useSchoolPlans;
